import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Brain } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../utils/api';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      subscription.unsubscribe();
    };
  }, []);
  
  const navLinks = [
    { name: 'Inicio', path: '/' },
    { name: 'Noticias', path: '/noticias' },
    { name: 'App Store', path: '/apps' },
    { name: 'Planes', path: '/planes' },
    { name: 'Nosotros', path: '/nosotros' },
  ];
  
  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'glass py-4 shadow-2xl' : 'bg-transparent py-6'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group" data-testid="logo-link">
            <div className="relative">
              <div className="absolute inset-0 bg-[#C6FF00] blur-xl opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>
              <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center border border-white/10 group-hover:border-[#C6FF00]/50 transition-colors relative">
                <Brain className="w-6 h-6 text-white group-hover:text-[#C6FF00] transition-colors" />
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-black tracking-tighter leading-none">BUILD</span>
              <span className="text-xs font-black tracking-[0.4em] text-[#C6FF00] leading-none mt-1">BRAINS</span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-xs font-black uppercase tracking-widest transition-all duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#C6FF00]'
                    : 'text-white/50 hover:text-white'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className="absolute -bottom-2 left-0 right-0 h-px bg-[#C6FF00]"
                  />
                )}
              </Link>
            ))}
            <Link
              to={user ? "/panel" : "/login"}
              className="px-6 py-2 rounded-full border border-white/10 text-white font-black text-xs uppercase tracking-widest hover:bg-[#C6FF00] hover:text-black hover:border-transparent transition-all duration-500 group flex items-center gap-2"
              data-testid="portal-link"
            >
              <div className={`w-1.5 h-1.5 rounded-full ${user ? 'bg-[#C6FF00] group-hover:bg-black' : 'bg-white/20'}`} />
              {user ? 'Mi Panel' : 'Iniciar Sesión'}
            </Link>
          </div>
          
          <button
            className="md:hidden w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden absolute top-full left-0 right-0 glass border-t border-white/5 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-10 space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block text-2xl font-black tracking-tighter ${
                    location.pathname === link.path
                      ? 'text-[#C6FF00]'
                      : 'text-white/40'
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-6 border-t border-white/5">
                <Link
                  to={user ? "/panel" : "/login"}
                  onClick={() => setIsOpen(false)}
                  className="block w-full py-5 rounded-2xl bg-[#C6FF00] text-black font-black text-center uppercase tracking-widest text-sm italic"
                  data-testid="mobile-admin-link"
                >
                  {user ? 'ACCEDER AL PANEL' : 'INICIAR SESIÓN'}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};